import { gql } from "@apollo/client";

// Hand-rolled operation documents + result interfaces for the announcement
// module (escape-hatch style, precedent: hooks/useTenantProducts.ts). Once the
// backend schema is deployed, `npm run codegen` regenerates the global types;
// these local interfaces mirror the schema exactly and can then be replaced.

/** One announcement as seen by the current user, with their interaction state. */
export interface MyAnnouncement {
  id: string;
  title: string;
  message: string;
  link: string | null;
  linkLabel: string | null;
  startDate: string;
  endDate: string;
  highlight: boolean;
  createdAt: string;
  readAt: string | null;
  clickedAt: string | null;
  dismissedAt: string | null;
}

export interface MyAnnouncementsPage {
  records: MyAnnouncement[];
  page: number;
  perPage: number;
  totalCount: number;
}

export interface TMyAnnouncementsQuery {
  announcement: { getMyAnnouncements: MyAnnouncementsPage | null };
}

export const myAnnouncementsQuery = gql`
  query myAnnouncements($page: Int, $perPage: Int) {
    announcement {
      getMyAnnouncements(page: $page, perPage: $perPage) {
        records {
          id
          title
          message
          link
          linkLabel
          startDate
          endDate
          highlight
          createdAt
          readAt
          clickedAt
          dismissedAt
        }
        page
        perPage
        totalCount
      }
    }
  }
`;

/** Who an announcement reaches. */
export type TAnnouncementAudience = "EVERYONE" | "TARGETED";

/** One tenant reached by a targeted announcement (every user of it). */
export interface AnnouncementTargetTenant {
  tenantId: string;
  tenantName: string | null;
}

/** One person reached individually; also the shape of a recipient candidate. */
export interface AnnouncementTargetUser {
  userId: string;
  userName: string | null;
  userEmail: string | null;
  tenantName: string | null;
}

/** Management-listing row: every announcement regardless of window, with author names. */
export interface AnnouncementManagementRow {
  id: string;
  title: string;
  message: string;
  link: string | null;
  linkLabel: string | null;
  startDate: string;
  endDate: string;
  highlight: boolean;
  audience: TAnnouncementAudience;
  targetTenants: AnnouncementTargetTenant[];
  targetUsers: AnnouncementTargetUser[];
  createdAt: string;
  createdByUserId: string | null;
  createdByName: string | null;
  updatedByUserId: string | null;
  updatedByName: string | null;
}

export interface TAnnouncementsForManagementQuery {
  announcement: {
    getAnnouncementsForManagement: {
      records: AnnouncementManagementRow[];
      page: number;
      perPage: number;
      totalCount: number;
    } | null;
  };
}

export const announcementsForManagementQuery = gql`
  query announcementsForManagement($page: Int, $perPage: Int) {
    announcement {
      getAnnouncementsForManagement(page: $page, perPage: $perPage) {
        records {
          id
          title
          message
          link
          linkLabel
          startDate
          endDate
          highlight
          audience
          targetTenants {
            tenantId
            tenantName
          }
          targetUsers {
            userId
            userName
            userEmail
            tenantName
          }
          createdAt
          createdByUserId
          createdByName
          updatedByUserId
          updatedByName
        }
        page
        perPage
        totalCount
      }
    }
  }
`;

export interface AnnouncementStats {
  announcementId: string;
  readCount: number;
  clickCount: number;
}

export interface TAnnouncementStatsQuery {
  announcement: { getAnnouncementStats: AnnouncementStats[] | null };
}

export const announcementStatsQuery = gql`
  query announcementStats($announcementIds: [ID!]!) {
    announcement {
      getAnnouncementStats(announcementIds: $announcementIds) {
        announcementId
        readCount
        clickCount
      }
    }
  }
`;

export interface AnnouncementInteraction {
  userId: string;
  userName: string;
  userEmail: string;
  tenantName: string;
  readAt: string | null;
  clickedAt: string | null;
  dismissedAt: string | null;
}

export interface TAnnouncementInteractionsQuery {
  announcement: {
    getAnnouncementInteractions: {
      records: AnnouncementInteraction[];
      page: number;
      perPage: number;
      totalCount: number;
    } | null;
  };
}

export const announcementInteractionsQuery = gql`
  query announcementInteractions($announcementId: ID!, $page: Int, $perPage: Int) {
    announcement {
      getAnnouncementInteractions(announcementId: $announcementId, page: $page, perPage: $perPage) {
        records {
          userId
          userName
          userEmail
          tenantName
          readAt
          clickedAt
          dismissedAt
        }
        page
        perPage
        totalCount
      }
    }
  }
`;

export interface AnnouncementManager {
  userId: string;
  userName: string;
  userEmail: string;
  grantedAt: string;
  grantedByUserId: string | null;
  grantedByName: string | null;
}

export interface TAnnouncementManagersQuery {
  announcement: { getAnnouncementManagers: AnnouncementManager[] | null };
}

export const announcementManagersQuery = gql`
  query announcementManagers {
    announcement {
      getAnnouncementManagers {
        userId
        userName
        userEmail
        grantedAt
        grantedByUserId
        grantedByName
      }
    }
  }
`;

/** An admin user eligible to receive the manage permission (grant picker). */
export interface AnnouncementManagerCandidate {
  userId: string;
  userName: string;
  userEmail: string;
}

export interface TAnnouncementManagerCandidatesQuery {
  announcement: { getAnnouncementManagerCandidates: AnnouncementManagerCandidate[] | null };
}

export const announcementManagerCandidatesQuery = gql`
  query announcementManagerCandidates {
    announcement {
      getAnnouncementManagerCandidates {
        userId
        userName
        userEmail
      }
    }
  }
`;

export interface TAnnouncementRecipientSearchQuery {
  announcement: {
    searchAnnouncementRecipients: {
      records: AnnouncementTargetUser[];
      page: number;
      perPage: number;
      totalCount: number;
    } | null;
  };
}

/** Recipient picker of the form: users of any tenant, searched by name or email. */
export const announcementRecipientSearchQuery = gql`
  query announcementRecipientSearch($search: String, $page: Int, $perPage: Int) {
    announcement {
      searchAnnouncementRecipients(search: $search, page: $page, perPage: $perPage) {
        records {
          userId
          userName
          userEmail
          tenantName
        }
        page
        perPage
        totalCount
      }
    }
  }
`;

export interface TMyStaffPermissionsQuery {
  announcement: { getMyStaffPermissions: string[] | null };
}

/** Staff permission alias granting access to the notifications admin area. */
export const ANNOUNCEMENTS_MANAGE = "ANNOUNCEMENTS_MANAGE";

export const myStaffPermissionsQuery = gql`
  query myStaffPermissions {
    announcement {
      getMyStaffPermissions
    }
  }
`;
